import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ColorScheme = 'light' | 'dark';
/** What the user *asked* for; `auto` follows the OS `prefers-color-scheme`. */
export type ThemePreference = 'light' | 'dark' | 'auto';
export type TableDensity = 'compact' | 'comfortable';

/**
 * Path the desktop shell lands on after sign-in / when visiting `/`. Kept as a
 * small allow-list (rather than a free string) so a stale persisted value can
 * never point at a route that no longer exists. `/` is the dashboard.
 */
export const LANDING_PATHS = ['/', '/find', '/inventory', '/acquisitions', '/alerts'] as const;
export type LandingPath = (typeof LANDING_PATHS)[number];

/** Resolve a {@link ThemePreference} to a concrete scheme, reading the OS only for `auto`. */
export function systemColorScheme(): ColorScheme {
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function resolvePreference(pref: ThemePreference): ColorScheme {
  return pref === 'auto' ? systemColorScheme() : pref;
}

interface UiState {
  /**
   * The *effective* scheme applied to the app (what `MantineProvider`'s
   * `forceColorScheme` reads). Always concrete light/dark — never `auto`. When
   * `themePreference` is `auto` this is kept in sync with the OS by an effect
   * in `main.tsx`.
   */
  colorScheme: ColorScheme;
  /** The user's chosen preference, including `auto`. Surfaced on the Appearance page. */
  themePreference: ThemePreference;
  navCollapsed: boolean;
  tableDensity: TableDensity;
  /** When true, the app suppresses non-essential animations/transitions. */
  reduceMotion: boolean;
  /** Play a short confirmation tone on every successful barcode/QR decode. */
  scanSound: boolean;
  /** Warn (native beforeunload) before leaving a page that has unsaved form edits. */
  warnUnsaved: boolean;
  /** Where the desktop shell sends you from `/`. */
  defaultLandingPath: LandingPath;

  setColorScheme: (scheme: ColorScheme) => void;
  toggleColorScheme: () => void;
  setThemePreference: (pref: ThemePreference) => void;
  setNavCollapsed: (collapsed: boolean) => void;
  toggleNav: () => void;
  setTableDensity: (density: TableDensity) => void;
  setReduceMotion: (reduce: boolean) => void;
  setScanSound: (on: boolean) => void;
  setWarnUnsaved: (on: boolean) => void;
  setDefaultLandingPath: (path: LandingPath) => void;
}

/**
 * UI-preferences store: color scheme, sidebar-collapsed state, table density,
 * and the device-local experience toggles surfaced on the Appearance settings
 * page (reduce-motion, scan sound, unsaved-changes guard, default landing).
 * Persisted to localStorage so preferences survive reloads.
 *
 * Kept separate from `useSession` because these are device-local UX
 * preferences, not server-backed identity. Both stores can be read
 * independently by any shell.
 *
 * Theme model: `themePreference` is the source of truth for what the user
 * chose (`light | dark | auto`); `colorScheme` is the concrete scheme actually
 * applied. The top-bar sun/moon quick-toggle and the Appearance page both keep
 * the two in lock-step via the setters here, and `main.tsx` re-resolves
 * `auto` against the OS media query on change.
 */
export const useUi = create<UiState>()(
  persist(
    (set) => ({
      colorScheme: 'light',
      themePreference: 'light',
      navCollapsed: false,
      tableDensity: 'comfortable',
      reduceMotion: false,
      scanSound: false,
      warnUnsaved: true,
      defaultLandingPath: '/',

      setColorScheme: (scheme) => set({ colorScheme: scheme }),
      toggleColorScheme: () =>
        set((s) => {
          const next: ColorScheme = s.colorScheme === 'light' ? 'dark' : 'light';
          // A manual flip is an explicit choice, so it pins the preference too —
          // otherwise an `auto` user could click the toggle and have the OS
          // immediately overrule them on the next media-query change.
          return { colorScheme: next, themePreference: next };
        }),
      setThemePreference: (pref) =>
        set({ themePreference: pref, colorScheme: resolvePreference(pref) }),
      setNavCollapsed: (collapsed) => set({ navCollapsed: collapsed }),
      toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
      setTableDensity: (density) => set({ tableDensity: density }),
      setReduceMotion: (reduce) => set({ reduceMotion: reduce }),
      setScanSound: (on) => set({ scanSound: on }),
      setWarnUnsaved: (on) => set({ warnUnsaved: on }),
      setDefaultLandingPath: (path) => set({ defaultLandingPath: path }),
    }),
    {
      name: 'mtb-storefront-ui',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
